import React, { FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Translate from 'ui/atoms/translate';
import WithDataRecord from 'hoc/WithDataRecord';
import {
  AdminApi,
  AdminPayoutEvent,
  AdminProductListing,
  AdminProductsListPermissionEnum,
  ProductPermissionEnum,
  AdminPayoutEventsListOrderingEnum,
} from 'api';
import PayoutEventsTable from './payout-events-table';
import Button from 'ui/atoms/button';
import AddPayoutModal from './add-payout-modal';
import useApiCall from 'src/hooks/use-api-call';
import DeletePayoutModal from './delete-payout-modal';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import { debounce } from 'lodash';
import { hasDistributionViewDataPermission } from '../../helpers/distributionPlatformPermissions';
import { hasViewPermission } from '../../helpers/tokenPermissions';
import Spacer from 'src/ui/atoms/spacer';
import PageWrapper from 'src/core/layout/content/page-wrapper';

interface IssuerPayoutEventsTableProps {
  status?: string;
  hasPermission: boolean;
  title?: ReactNode;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  filters?: ReactNode;
  onEditButtonClick: (payoutEvent: AdminPayoutEvent) => void;
  onDeleteButtonClick: (payoutEvent: AdminPayoutEvent) => void;
  setFilterPopupHeight?: (height: number) => void;
}

const IssuerPayoutEventsTable: FunctionComponent<IssuerPayoutEventsTableProps> = WithDataRecord<
  AdminApi,
  AdminPayoutEvent
>(AdminApi)(
  ({
    data,
    ordering,
    onOrderBy,
    loading,
    hasPermission,
    paginationProps,
    onEditButtonClick,
    onDeleteButtonClick,
    setFilterPopupHeight,
  }) => {
    return (
      <>
        <PayoutEventsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          payoutEvents={data}
          hasPermission={hasPermission}
          loading={loading}
          paginationProps={paginationProps}
          onEditButtonClick={onEditButtonClick}
          onDeleteButtonClick={onDeleteButtonClick}
          setFilterPopupHeight={setFilterPopupHeight}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminPayoutEventsList({
      offset,
      limit,
      ordering: ordering as AdminPayoutEventsListOrderingEnum,
      ...props,
    });
  },
);

const IssuerPayoutEventsPage: FunctionComponent = () => {
  const { withApi, makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const [products, setProducts] = useState<AdminProductListing[]>([]);
  const [isAddPayoutEventOpen, setAddPayoutEventOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [editPayoutEventModalOpen, setEditPayoutEventModalOpen] = useState<AdminPayoutEvent | null>(null);
  const [deletePayoutEventModalOpen, setDeletePayoutEventModalOpen] = useState<AdminPayoutEvent | null>(null);
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  const { currentUser } = useCurrentUserSelector();

  const hasPermission =
    hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
    hasViewPermission(currentUser);

  const fetchProducts = useCallback(
    (searchQuery: string = '', action?: string) => {
      withApi(async () => {
        if (action === 'menu-close' && searchQuery === '') {
          return;
        }
        const response = await adminApi.adminProductsList({
          permission: AdminProductsListPermissionEnum.MANAGE_PAYOUT_EVENTS,
          search: searchQuery,
          limit: 100,
        });
        setProducts(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const searchProductValue = useCallback(
    (value: string, action?: string) => {
      fetchProducts(value, action);
    },
    [fetchProducts],
  );

  const debouncedProductSearch = useMemo(() => debounce(searchProductValue, 500), [searchProductValue]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const refreshPayoutTable = useCallback(() => {
    setEditPayoutEventModalOpen(null);
    setDeletePayoutEventModalOpen(null);
    setAddPayoutEventOpen(false);
    setForceDataLoad(true);
  }, [forceDataLoad]);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboard.issuerPayoutEvents" />} titleSpacing="larger">
        {currentUser?.permissionOnAtLeastOneProduct.includes(ProductPermissionEnum.MANAGE_PAYOUT_EVENTS) && (
          <>
            <Button
              variant="primary"
              icon="plus"
              size="small"
              iconSize="small"
              iconColor="inherit"
              onClick={() => setAddPayoutEventOpen(true)}
            >
              <Translate name="studioPayoutEvents.addPayout" />
            </Button>
            <Spacer y={4} />
          </>
        )}
        <IssuerPayoutEventsTable
          forceDataLoad={forceDataLoad}
          setForceDataLoad={setForceDataLoad}
          hasPermission={hasPermission}
          onEditButtonClick={(payoutEvent: AdminPayoutEvent) => setEditPayoutEventModalOpen(payoutEvent)}
          onDeleteButtonClick={(payoutEvent: AdminPayoutEvent) => setDeletePayoutEventModalOpen(payoutEvent)}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {isAddPayoutEventOpen && (
          <AddPayoutModal
            products={products}
            onSavePayout={refreshPayoutTable}
            onClose={() => setAddPayoutEventOpen(false)}
            onSearchProduct={debouncedProductSearch}
          />
        )}
        {!!editPayoutEventModalOpen && (
          <AddPayoutModal
            products={products}
            onSavePayout={refreshPayoutTable}
            onClose={() => setEditPayoutEventModalOpen(null)}
            data={editPayoutEventModalOpen}
            onSearchProduct={debouncedProductSearch}
          />
        )}
        {!!deletePayoutEventModalOpen && (
          <DeletePayoutModal
            onDeletePayout={refreshPayoutTable}
            onClose={() => setDeletePayoutEventModalOpen(null)}
            data={deletePayoutEventModalOpen}
          />
        )}
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerPayoutEventsPage, AppType.ISSUER);
